import {
  getGetOralExamIdPhotoUrlQueryKey,
  useGetOralExamIdPhotoUrl,
} from '@/common/api/endpoints/oral-exams/oral-exams';
import type { GetOralExamIdPhotoUrlKind } from '@/common/api/models/getOralExamIdPhotoUrlKind';
import { useOralExamLabOverrides } from '@/common/oralExam/oralExamLabOverrides';

export type IdPhotoKind = GetOralExamIdPhotoUrlKind;

export type IdPhotoUrlResult = {
  url: string | null;
  /** React Query dataUpdatedAt — changes on every successful refetch. */
  revision: number;
};

/** Query key including kind so document and selfie SAS URLs cache independently. */
export function getIdPhotoUrlQueryKey(slotId: string, bookingId: string, kind: IdPhotoKind) {
  return getGetOralExamIdPhotoUrlQueryKey(slotId, bookingId, { kind });
}

/** Refetch SAS URLs before the 1h backend expiry (50 min keeps a comfortable margin). */
export const ID_PHOTO_SAS_REFETCH_INTERVAL_MS = 50 * 60 * 1000;

/** Append a cache-busting query param so browser Cache-Control on SAS URLs cannot serve stale bytes. */
export function idPhotoUriWithCacheBust(url: string, revision: number): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}flare_rev=${revision}`;
}

/**
 * Resolves a student ID photo blob path to a short-lived SAS URL via the oral exam API.
 * Returns null when no blob path is provided or when the backend has no URL to serve.
 *
 * Cache invalidation on resubmit is handled by {@link useOralExamCohortState} when the
 * cohort poll observes idVerification.status transitioning back to `submitted`.
 */
export function useIdPhotoUrl(
  slotId: string,
  bookingId: string,
  blobPath: string | null | undefined,
  kind: IdPhotoKind = 'document',
): IdPhotoUrlResult {
  const lab = useOralExamLabOverrides();
  const labUrl = blobPath ? (lab?.resolveIdPhotoUrl?.(bookingId, kind) ?? null) : null;
  const { data, dataUpdatedAt } = useGetOralExamIdPhotoUrl(slotId, bookingId, { kind }, {
    query: {
      enabled: !!blobPath && !labUrl,
      refetchInterval: blobPath && !labUrl ? ID_PHOTO_SAS_REFETCH_INTERVAL_MS : false,
    },
  });
  // A disabled query (blobPath now null/undefined, e.g. cleared on rejection or
  // reset-on-disconnect) does NOT clear React Query's previously cached `data` —
  // it just stops refetching. Without this guard the lecturer would keep seeing
  // the last photo forever after it's supposed to disappear. The blob path is the
  // source of truth for "is there a photo to show", not the query cache.
  return {
    url: blobPath ? (labUrl ?? data?.data?.data?.url ?? null) : null,
    revision: labUrl ? 0 : dataUpdatedAt,
  };
}
